import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useChain, useUser } from '@/stores';
import { getWalletForChain, onboardingRouteForChain } from '@/utils/wallets';

interface RequireWalletProps {
  children: ReactNode;
}

/**
 * Component that checks if the authenticated user has a wallet address
 * Shows message if wallet is missing
 */
export function RequireWallet({ children }: RequireWalletProps) {
  const user = useUser();
  const { selectedChain } = useChain();

  const wallet = getWalletForChain(user, selectedChain);
  const hasWallet = wallet?.address;
  const operationalStatus = wallet?.operational_status || 'active';

  if (!hasWallet) {
    return <Navigate to={onboardingRouteForChain(selectedChain)} replace />;
  }

  if (operationalStatus === 'unavailable' || operationalStatus === 'revoked') {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-red-900/40 bg-red-950/20 p-6 text-red-100">
        <h1 className="text-xl font-semibold">Managed wallet unavailable</h1>
        <p className="mt-2 text-sm text-red-200/80">
          Your account still points at a managed wallet record, but custody can no longer access its credential.
          Trading is blocked until this wallet is recovered or replaced.
        </p>
      </div>
    );
  }

  if (operationalStatus === 'unknown') {
    return (
      <div className="mx-auto max-w-2xl rounded-2xl border border-amber-400/30 bg-amber-500/10 p-6 text-amber-50">
        <h1 className="text-xl font-semibold">Managed wallet status unavailable</h1>
        <p className="mt-2 text-sm text-amber-100/80">
          The app could not confirm the current wallet status with custody. Trading is blocked until the wallet
          status can be verified.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
